import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  RefreshCw,
  Snowflake,
  X,
  Loader2,
  IndianRupee,
  Megaphone,
  Zap,
  Bell,
  Lock,
  MessageCircle,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  getWallet,
  getWalletTransactions,
  getMyPrices,
  updateWalletSettings,
  createRechargeOrder,
  verifyRecharge,
  getWalletSpendSummary,
} from "../apis/wallet";
import { useBranding } from "../contexts/BrandingContext";
import { resolveCheckoutBrand } from "../utils/checkoutBrand";
import { fmtINR } from "../utils/money";
import { fmtIST } from "../utils/datetime";
import { isCredit, parseMeta, txnSub, txnTitle } from "../utils/walletTxn";
import PaymentBreakdown from "../components/PaymentBreakdown";
import { formatPayable } from "../utils/paymentCharges";

const QUICK_AMOUNTS = [500, 1000, 2000, 5000, 10000];
const PAGE_SIZE = 20;

const RATE_ROWS = [
  { key: "marketing", label: "Marketing", desc: "Promotions and offers", bg: "#E7F3FF", color: "#0064E0", Icon: Megaphone },
  { key: "marketing_lite", label: "Marketing Lite", desc: "Simple promotional sends", bg: "#E7F7F3", color: "#0F766E", Icon: Zap },
  { key: "utility", label: "Utility", desc: "Order updates and alerts", bg: "#FFF4E5", color: "#B77400", Icon: Bell },
  { key: "authentication", label: "Authentication", desc: "One-time passcodes", bg: "#FDECEF", color: "#E41E3F", Icon: Lock },
  { key: "service", label: "Service", desc: "Replies inside the 24-hour window", bg: "#F3E8FD", color: "#7E57C2", Icon: MessageCircle },
];

const Wallet = () => {
  const brand = useBranding();
  const checkout = resolveCheckoutBrand(brand);
  const [wallet, setWallet] = useState(null);
  const [prices, setPrices] = useState(null);
  const [txns, setTxns] = useState({ rows: [], total: 0 });
  const [spend, setSpend] = useState(null);
  const [page, setPage] = useState(0);
  const [typeFilter, setTypeFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [txnLoading, setTxnLoading] = useState(false);

  // Recharge modal
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [paying, setPaying] = useState(false);

  // Threshold editor
  const [thresholdDraft, setThresholdDraft] = useState("");
  const [savingThreshold, setSavingThreshold] = useState(false);
  const [editingThreshold, setEditingThreshold] = useState(false);

  const loadWallet = useCallback(async () => {
    try {
      const [wRes, pRes] = await Promise.all([getWallet(), getMyPrices("IN")]);
      if (wRes.data?.success) {
        setWallet(wRes.data.data);
        setThresholdDraft(String(wRes.data.data.low_balance_threshold ?? 100));
      }
      if (pRes.data?.success) setPrices(pRes.data.data.prices);
    } catch (err) {
      toast.error("Failed to load wallet");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTxns = useCallback(async () => {
    setTxnLoading(true);
    try {
      const res = await getWalletTransactions({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        ...(typeFilter ? { type: typeFilter } : {}),
      });
      if (res.data?.success) setTxns(res.data.data);
    } catch (_) {
    } finally {
      setTxnLoading(false);
    }
  }, [page, typeFilter]);

  const loadSpend = useCallback(async () => {
    try {
      const res = await getWalletSpendSummary();
      if (res.data?.success) setSpend(res.data.data);
    } catch (_) {}
  }, []);

  useEffect(() => { loadWallet(); loadSpend(); }, [loadWallet, loadSpend]);
  useEffect(() => { loadTxns(); }, [loadTxns]);

  const startRecharge = async () => {
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt < 100) {
      toast.error("Minimum recharge is ₹100");
      return;
    }
    if (!window.Razorpay) {
      toast.error("Payment SDK not loaded. Refresh the page and try again.");
      return;
    }
    setPaying(true);
    try {
      const orderRes = await createRechargeOrder(amt);
      const order = orderRes.data?.data;
      if (!order?.order_id) throw new Error("Failed to create order");

      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: checkout.name,
        image: checkout.image || undefined,
        description: "Wallet recharge",
        order_id: order.order_id,
        theme: { color: "#0064E0" },
        handler: async (response) => {
          try {
            const verifyRes = await verifyRecharge({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            if (verifyRes.data?.success) {
              toast.success(`Wallet credited ${fmtINR(amt)}. New balance ${fmtINR(verifyRes.data.data.balance)}`);
              setRechargeOpen(false);
              setAmount("");
              loadWallet();
              setPage(0);
              loadTxns();
            } else {
              toast.error("Payment verification failed");
            }
          } catch (err) {
            toast.error(err.response?.data?.message || "Payment verification failed");
          } finally {
            setPaying(false);
          }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.on("payment.failed", (resp) => {
        setPaying(false);
        toast.error(resp.error?.description || "Payment failed");
      });
      rzp.open();
    } catch (err) {
      setPaying(false);
      toast.error(err.response?.data?.error || err.message || "Failed to start recharge");
    }
  };

  const saveThreshold = async () => {
    const v = Number(thresholdDraft);
    if (!Number.isFinite(v) || v < 0) {
      toast.error("Invalid threshold");
      return;
    }
    setSavingThreshold(true);
    try {
      await updateWalletSettings({ low_balance_threshold: v });
      toast.success("Low-balance alert threshold saved");
      loadWallet();
      return true;
    } catch (_) {
      toast.error("Failed to save threshold");
      return false;
    } finally {
      setSavingThreshold(false);
    }
  };

  const totalPages = Math.max(Math.ceil(txns.total / PAGE_SIZE), 1);
  const lowBalance = wallet && wallet.balance < wallet.low_balance_threshold;

  const marketingRate = Number(prices?.marketing || 0);
  const approxMessages =
    marketingRate > 0 && wallet?.balance > 0
      ? Math.floor(Number(wallet.balance) / marketingRate)
      : 0;

  const openRecharge = (preset) => {
    if (preset != null) setAmount(String(preset));
    setRechargeOpen(true);
  };

  if (loading) {
    return (
      <div className="wallet-page" style={{ display: "grid", placeItems: "center" }}>
        <Loader2 className="animate-spin" size={28} style={{ color: "var(--blue)" }} />
      </div>
    );
  }

  return (
    <div className="wallet-page">
      <div className="page-head">
        <div>
          <h2>Wallet</h2>
          <p>Prepaid balance for WhatsApp messaging</p>
        </div>
        <div className="grow" />
        <button
          type="button"
          onClick={() => { loadWallet(); loadTxns(); loadSpend(); }}
          className="ping-btn ping-btn-ghost ping-btn-sm"
        >
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      <div className="wallet-grid">
        <div className="wallet-col">
          <div className="wallet-balance">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="k">Available balance</div>
                <div className="v tabular-nums">{fmtINR(wallet?.balance)}</div>
                {Number(wallet?.balance) < 0 && (
                  <div className="d" style={{ color: "var(--red)" }}>
                    Overdrawn from messages already delivered. Recharge to clear this; new broadcasts stay blocked until the balance is positive.
                  </div>
                )}
                {approxMessages > 0 && (
                  <div className="d">
                    Covers roughly {approxMessages.toLocaleString("en-IN")} marketing messages
                  </div>
                )}
              </div>
              {wallet?.status === "frozen" && (
                <span className="ping-chip a">
                  <Snowflake size={12} /> Frozen
                </span>
              )}
            </div>

            {editingThreshold ? (
              <div className="flex items-center gap-2 mt-3">
                <div className="relative">
                  <IndianRupee size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 opacity-70" />
                  <input
                    type="number"
                    min="0"
                    value={thresholdDraft}
                    onChange={(e) => setThresholdDraft(e.target.value)}
                    className="w-28 rounded-lg pl-7 pr-2 py-1.5 text-sm text-gray-900"
                  />
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    const ok = await saveThreshold();
                    if (ok) setEditingThreshold(false);
                  }}
                  disabled={savingThreshold}
                  className="ping-btn ping-btn-primary ping-btn-sm disabled:opacity-50"
                >
                  {savingThreshold ? "Saving…" : "Save"}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingThreshold(false)}
                  className="ping-btn ping-btn-ghost ping-btn-sm"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="d" style={{ marginTop: 6 }}>
                Low-balance alert at ₹{Number(wallet?.low_balance_threshold ?? 100).toFixed(0)}
                {" · "}
                <button type="button" onClick={() => setEditingThreshold(true)}>Edit</button>
                {lowBalance ? " · Balance is below this amount" : ""}
              </div>
            )}

            <div className="wallet-quick">
              {QUICK_AMOUNTS.map((v) => (
                <button
                  key={v}
                  type="button"
                  className={String(v) === amount ? "on" : ""}
                  onClick={() => openRecharge(v)}
                >
                  ₹{v.toLocaleString("en-IN")}
                </button>
              ))}
            </div>
            <div className="wallet-balance-row">
              <button type="button" className="ping-btn ping-btn-sm solid" onClick={() => openRecharge()}>
                <Plus size={14} />
                Add money
              </button>
            </div>
          </div>

          <div className="wallet-card wallet-rates-card">
            <div className="wallet-card-pad">
              <h3>Your message rates</h3>
              <div className="sub">Per message delivered, India</div>
              <ul className="wallet-rates">
                {RATE_ROWS.map(({ key, label, desc, bg, color, Icon }) => {
                  const isService = key === "service";
                  const overage = Number(prices?.utility || 0);
                  return (
                    <li key={key}>
                      <span className="rc" style={{ background: bg, color }}>
                        <Icon size={15} />
                      </span>
                      <div>
                        <b>{label}</b>
                        <small>
                          {isService ? "First 1,000 free each month, per number" : desc}
                        </small>
                      </div>
                      <span className="amt tabular-nums" style={isService ? { textAlign: "right", lineHeight: 1.15 } : undefined}>
                        {isService ? (
                          <>
                            {overage > 0 ? fmtINR(overage) : "—"}
                            <small style={{ display: "block", fontWeight: 600, fontSize: 11, color: "var(--text-2)" }}>
                              after 1,000
                            </small>
                          </>
                        ) : (
                          prices?.[key] > 0 ? fmtINR(prices[key]) : "Free"
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>

        <div className="wallet-col">
        <div className="wallet-card wallet-spend-strip">
          <div className="wallet-activity-head">
            <h3 style={{ flex: 1 }}>Campaign spend</h3>
            <Link to="/wallet/spend" className="ping-btn ping-btn-ghost ping-btn-sm">See all</Link>
          </div>
          {spend?.campaigns?.length ? (
            <div className="wspend-strip-list">
              {spend.campaigns.filter((c) => c.campaign_id).slice(0, 2).map((c) => (
                <Link key={c.campaign_id} to={`/wallet/spend/${c.campaign_id}`} className="wspend-strip-item">
                  <b>{c.campaign_name}</b>
                  <span>{c.billed_n.toLocaleString("en-IN")} billed · {fmtINR(c.net)} net</span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="wallet-empty" style={{ minHeight: 72 }}>No campaign charges yet</div>
          )}
        </div>

        <div className="wallet-card wallet-activity">
          <div className="wallet-activity-head">
            <h3 style={{ flex: 1 }}>Recent activity</h3>
            <Link to="/wallet/spend?view=txns" className="auth-link" style={{ fontSize: 13, marginRight: 8 }}>
              See all
            </Link>
            <select
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
            >
              <option value="">All types</option>
              <option value="recharge">Recharges</option>
              <option value="debit">Message charges</option>
              <option value="refund">Refunds</option>
              <option value="adjustment">Adjustments</option>
            </select>
          </div>

          <div className="wallet-activity-body">
            {txnLoading ? (
              <div className="wallet-empty">
                <Loader2 className="animate-spin mx-auto" size={22} style={{ color: "var(--blue)" }} />
              </div>
            ) : txns.rows.length === 0 ? (
              <div className="wallet-empty">No transactions yet</div>
            ) : (
              <table className="wallet-table">
                <thead>
                  <tr>
                    <th>Transaction</th>
                    <th>Date</th>
                    <th style={{ textAlign: "right" }}>Amount</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {txns.rows.map((t) => {
                    const meta = parseMeta(t.meta);
                    const credit = isCredit(t.type, meta, t.sign);
                    return (
                      <tr key={t.id}>
                        <td>
                          <div className="wallet-cell">
                            <span className={`wallet-dir ${credit ? "cr" : "db"}`} aria-hidden>
                              <i />
                            </span>
                            <div>
                              <b>{txnTitle(t.type, meta, t.resolved)}</b>
                              <span className="sub">{txnSub(t.type, meta, t.reference_id, t.resolved)}</span>
                            </div>
                          </div>
                        </td>
                        <td style={{ color: "var(--text-2)", whiteSpace: "nowrap" }}>
                          {fmtIST(t.created_at)}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <span className={credit ? "wallet-amt-cr" : "wallet-amt-db"}>
                            {credit ? "+" : "−"} {fmtINR(t.amount)}
                          </span>
                        </td>
                        <td style={{ textAlign: "right", width: 1, whiteSpace: "nowrap" }}>
                          <Link to={`/wallet/transactions/${t.id}`} className="ping-btn ping-btn-ghost ping-btn-sm">
                            Details
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {txns.total > 0 && (
            <div className="wallet-pager">
              <span>Page {page + 1} of {totalPages} · {txns.total} transactions</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                  className="ping-btn ping-btn-ghost ping-btn-sm disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={page + 1 >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="ping-btn ping-btn-ghost ping-btn-sm disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>

      {rechargeOpen && (
        <>
          <div className="ping-drawer-backdrop" onClick={() => !paying && setRechargeOpen(false)} />
          <aside className="ping-drawer" role="dialog" aria-modal="true">
            <div className="ping-drawer-head">
              <div>
                <h3>Add money</h3>
                <p>Pay with Razorpay · UPI, cards, netbanking</p>
              </div>
              <button
                type="button"
                onClick={() => !paying && setRechargeOpen(false)}
                className="ping-btn ping-btn-ghost ping-btn-sm"
              >
                <X size={16} />
              </button>
            </div>
            <div className="ping-drawer-body">
              <label style={{ marginBottom: 10 }}>Choose an amount</label>
              <div className="wallet-amounts">
                {QUICK_AMOUNTS.map((v) => (
                  <button
                    key={v}
                    type="button"
                    className={String(v) === amount ? "on" : ""}
                    onClick={() => setAmount(String(v))}
                  >
                    ₹{v.toLocaleString("en-IN")}
                  </button>
                ))}
              </div>
              <div className="wallet-amount-box">
                <label htmlFor="wallet-amount">Custom amount</label>
                <div className="field">
                  <IndianRupee size={16} />
                  <input
                    id="wallet-amount"
                    type="number"
                    min="100"
                    placeholder="Min 100"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </div>
                <p className="wallet-credit-hint">This amount is credited to your wallet. GST and platform fee are added at checkout.</p>
              </div>
              <PaymentBreakdown base={amount} creditLabel="Wallet credit" />
            </div>
            <div className="ping-drawer-foot">
              <button
                type="button"
                onClick={() => !paying && setRechargeOpen(false)}
                className="ping-btn ping-btn-ghost ping-btn-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={startRecharge}
                disabled={paying || !amount}
                className="ping-btn ping-btn-primary ping-btn-sm disabled:opacity-50"
              >
                {paying ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                {paying
                  ? "Processing…"
                  : `Pay${amount && Number(amount) > 0 ? ` ₹${formatPayable(amount)}` : ""}`}
              </button>
            </div>
          </aside>
        </>
      )}
    </div>
  );
};

export default Wallet;
